import type { Editor } from '@tiptap/react';
import type { AiChange, AiChangesStorage } from '@tiptap-pro/extension-ai-changes';
import type { Slice } from '@tiptap/pm/model';

interface AiStorage {
  state?: string;
  response?: string;
  initialContent?: Slice | null;
  error?: Error;
  generatedWith?: { options?: { insert?: boolean; append?: boolean } };
  pastResponses?: unknown[];
}

export const getAiStorage = (editor: Editor): AiStorage | undefined =>
  (editor.storage.ai as AiStorage | undefined) ?? (editor.storage.aiAdvanced as AiStorage | undefined);

export const getAiChangesStorage = (editor: Editor): AiChangesStorage | undefined =>
  // AiChanges extension does not augment the @tiptap/core Storage interface,
  // so cast through unknown.
  (editor.extensionStorage as unknown as { aiChanges?: AiChangesStorage }).aiChanges;

const getAiChanges = (editor: Editor): AiChange[] => getAiChangesStorage(editor)?.getChanges() ?? [];

export const selectionOverlapsAiChange = (editor: Editor): boolean => {
  const { from, to } = editor.state.selection;
  return getAiChanges(editor).some((c) => c.newRange.from <= to && c.newRange.to >= from);
};

/**
 * Accept the pending AI response: keep the inserted content, clear the
 * AI Changes tracker, and reset AI storage.
 */
export const acceptPendingAi = (editor: Editor): void => {
  editor
    .chain()
    .acceptAllAiChanges()
    .aiAccept()
    .command(({ commands }) => commands.stopTrackingAiChanges())
    .focus()
    .run();
};

/**
 * Reject the pending AI response. Restores the pre-AI slice at the tracked
 * AI change range via a manual `tr.replace`, then clears AI storage.
 *
 * Why we don't just call `aiReject({type:'reset'})`: the extension's
 * internal replace uses the AI decoration range (`D(editor)`), which is
 * cleared when the user manually deletes the AI content. After delete+undo
 * the content is restored but the decoration isn't, so aiReject no-ops on
 * content and only clears storage — not what the user expects from a Reject
 * button. Reading the range from `aiChanges` (which tracks through undo)
 * lets us restore correctly.
 *
 * Storage cleanup uses `aiAccept`: aiAccept only re-inserts `response`
 * when `generatedWith.options.insert === false` (non-streaming mode).
 * Our callers always stream, so aiAccept here is storage-only. The
 * primary path can't use `aiReject` for cleanup because aiReject would
 * re-run its own `tr.replace` on top of ours.
 *
 * Fallback path (range invalid or initialContent missing) uses `aiReject`:
 * its internal replace is skipped when the decoration is null, and it
 * clears storage with the semantically correct "rejected" meta.
 */
export const rejectPendingAi = (editor: Editor): void => {
  const changes = getAiChanges(editor);
  const initialContent = getAiStorage(editor)?.initialContent;
  const docSize = editor.state.doc.content.size;

  const minFrom = changes.length > 0 ? Math.min(...changes.map((c) => c.newRange.from)) : null;
  const maxTo = changes.length > 0 ? Math.max(...changes.map((c) => c.newRange.to)) : null;

  const rangeValid = minFrom !== null && maxTo !== null && minFrom >= 0 && maxTo <= docSize;

  if (rangeValid && initialContent) {
    editor
      .chain()
      .command(({ tr }) => {
        tr.replace(minFrom, maxTo, initialContent);
        return true;
      })
      .aiAccept()
      .command(({ commands }) => commands.stopTrackingAiChanges())
      .focus()
      .run();
    return;
  }

  editor
    .chain()
    .aiReject()
    .command(({ commands }) => commands.stopTrackingAiChanges())
    .focus()
    .run();
};
